/**
 * Service Provider Context Store — Active role within the cabinet/firm
 * Roles: Associé, Directeur Mission, Consultant GRC, Expert Sécu, etc.
 * Persisted in localStorage, syncs across tabs via StorageEvent
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { GRC_CONTEXTS, type GrcContextId } from '@/config/contexts'

const DEFAULT_CONTEXT: GrcContextId = 'associe'

/**
 * Coerce any value to a valid context id. A stale / legacy / renamed id that is
 * no longer a key of GRC_CONTEXTS (e.g. persisted 'dm') would otherwise make
 * every `GRC_CONTEXTS[activeContext]` access return undefined and white-screen
 * the app at boot. Fall back to the default context instead.
 */
function coerceContextId(id: unknown): GrcContextId {
  return typeof id === 'string' && id in GRC_CONTEXTS
    ? (id as GrcContextId)
    : DEFAULT_CONTEXT
}

interface ContextState {
  activeContext: GrcContextId
  previousContext: GrcContextId | null
  setActiveContext: (id: GrcContextId) => void
}

export const useContextStore = create<ContextState>()(
  persist(
    (set, get) => ({
      activeContext: DEFAULT_CONTEXT,
      previousContext: null,

      setActiveContext: (id: GrcContextId) => {
        const next = coerceContextId(id)
        const current = get().activeContext
        if (current === next) return
        set({
          previousContext: current,
          activeContext: next,
        })
      },
    }),
    {
      name: 'sgrc-adminclient-active-context',
      partialize: (state) => ({ activeContext: state.activeContext }),
      // Sanitize the rehydrated value: if localStorage holds a stale/invalid id,
      // coerce it back to the default so downstream GRC_CONTEXTS[activeContext]
      // is always defined. The persisted correction is written on next change.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<ContextState>
        return {
          ...current,
          ...p,
          activeContext: coerceContextId(p.activeContext),
        }
      },
    }
  )
)
